// V1 source: customer/midas/components/DiagramInfoCard.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 floating tooltip — Tier A behavioral parity with V1. Logic copied
// verbatim: cursor-follow position with edge-flip + viewport clamp, dim
// measurement via callback ref, type-translation map, fallback rendering.
//
// V1 used inline styles only (no MUI). V2 keeps inline `style` for
// position/size (computed values) and adds a Tailwind className for the
// chrome (border/shadow/typography) so it inherits the V2 palette tokens.
//
// 2026-10-04 (ZAL10-OPTION-FIELD-1): when the hotspot carries the OPTIONAL
// `option` string, one more line is drawn at the very END of the card:
// "Option: <option>" (label i18n'd, value normalised by
// normalizeHotspotOption). No `option` = the card is exactly as before.
//
// 2026-10-06 (PARTS-SERVING-1): optional `type_label` replaces our type map
// word on the type line; optional `parts` / `details` / `links` are drawn
// between the description and the Option line (DiagramInfoCardExtrasV2).
// None of them present = the card is byte-identical to before (pinned by
// __tests__/DiagramInfoCardPartsV2.test.tsx).
//
// Two modes. HOVER (default): pointer-events:none, follows the cursor,
// clamped to the window — unchanged. PINNED (`interactive`, mounted by
// DiagramPinnedInfoCardV2 when a hotspot with parts/details/links is
// clicked): stays at the click point, clamped to `bounds` (the diagram
// viewport), scrolls inside `maxHeight`, and its sheet links are buttons.
//
// Every hotspot string on this card (`label`, `description`, `option`,
// `type_label`, every part / detail / link string) is a React text node —
// never innerHTML / dangerouslySetInnerHTML / markdown, so a printed caption
// can never become markup (SEC-REQ-5; pinned by
// __tests__/DiagramInfoCardV2.test.tsx + DiagramInfoCardPartsV2.test.tsx).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { DiagramHotspot } from "../../../../shared/chatBot/models/chatBotModels";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { normalizeHotspotOption } from "../../utils/diagramHotspotOption";
import {
  hasHotspotBodyExtras,
  parseHotspotExtras,
} from "../../utils/diagramHotspotParts";
import DiagramInfoCardExtrasV2 from "./DiagramInfoCardExtrasV2";

/** Clamp area in viewport (client) coordinates. */
export interface InfoCardBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface Props {
  hotspot: DiagramHotspot;
  mouseX: number;
  mouseY: number;
  /** PARTS-SERVING-1: pinned mode — clickable, scrollable, fixed in place. */
  interactive?: boolean;
  /** Pinned mode: clamp to this rect instead of the window. */
  bounds?: InfoCardBounds;
  /** Pinned mode: max card height (px); the card scrolls inside it. */
  maxHeight?: number;
  /** Pinned mode: open a linked sheet (page id) in the overlay viewer. */
  onOpenLink?: (pageId: number) => void;
}

const TOOLTIP_OFFSET = 8;
const MAX_WIDTH = 420;
// PARTS-SERVING-1: a HOVER card with part details can be long but cannot
// scroll (pointer-events:none) — cap it; the pinned card shows everything.
const HOVER_EXTRAS_MAX_HEIGHT = 360;
const CARD_CHROME =
  "border border-midas-stone bg-midas-bone-light text-midas-graphite shadow-[0_2px_8px_rgba(26,26,26,0.15)] font-sora";

// Map API type values to translation keys — verbatim from V1
const TYPE_TRANSLATION_KEYS: Record<string, string> = {
  relay: "midas.artifact.diagram.typeRelay",
  contactor: "midas.artifact.diagram.typeContactor",
  module: "midas.artifact.diagram.typeModule",
  terminal_block: "midas.artifact.diagram.typeTerminalBlock",
  cable: "midas.artifact.diagram.typeCable",
  switch: "midas.artifact.diagram.typeSwitch",
  sensor: "midas.artifact.diagram.typeSensor",
  circuit_breaker: "midas.artifact.diagram.typeCircuitBreaker",
  fuse: "midas.artifact.diagram.typeFuse",
  motor: "midas.artifact.diagram.typeMotor",
  transformer: "midas.artifact.diagram.typeTransformer",
  plc_module: "midas.artifact.diagram.typePlcModule",
  valve: "midas.artifact.diagram.typeValve",
};

const DiagramInfoCardV2: React.FC<Props> = ({
  hotspot,
  mouseX,
  mouseY,
  interactive = false,
  bounds,
  maxHeight,
  onOpenLink,
}) => {
  const { translate } = useTranslation("chatBot");
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  // Measure tooltip dimensions after render for accurate edge flipping
  const measuredRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      (tooltipRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      setDims({ w: node.offsetWidth, h: node.offsetHeight });
    }
  }, []);

  // Re-measure when content changes
  useEffect(() => {
    if (tooltipRef.current) {
      setDims({ w: tooltipRef.current.offsetWidth, h: tooltipRef.current.offsetHeight });
    }
  }, [hotspot]);

  const viewW = window.innerWidth;
  const viewH = window.innerHeight;
  // PARTS-SERVING-1: the pinned card clamps to the diagram viewport; the
  // hover card keeps the window (the exact original numbers).
  const area = bounds ?? { left: 0, top: 0, right: viewW, bottom: viewH };
  // Use measured dims; fallback to conservative estimate (not full MAX_WIDTH)
  const tw = dims.w > 0 ? dims.w : 200;
  const th = dims.h > 0 ? dims.h : 60;

  // Position at cursor + offset, flip when near edges
  let left = mouseX + TOOLTIP_OFFSET;
  let top = mouseY + TOOLTIP_OFFSET;

  if (left + tw > area.right) {
    left = mouseX - tw - TOOLTIP_OFFSET;
  }
  if (top + th > area.bottom) {
    top = mouseY - th - TOOLTIP_OFFSET;
  }

  // Clamp to viewport so tooltip never goes off-screen
  left = Math.max(area.left + 4, Math.min(left, area.right - tw - 4));
  top = Math.max(area.top + 4, Math.min(top, area.bottom - th - 4));

  // PARTS-SERVING-1: validated optional fields (all empty when absent).
  const extras = parseHotspotExtras(hotspot);
  const hasBodyExtras = hasHotspotBodyExtras(extras);

  // Translate type value using i18n keys, fallback to formatted raw value.
  // PARTS-SERVING-1: a served `type_label` (already localized) wins.
  const translationKey = hotspot.type ? TYPE_TRANSLATION_KEYS[hotspot.type] : undefined;
  const displayType = extras.typeLabel
    ? extras.typeLabel
    : translationKey
      ? translate(translationKey)
      : hotspot.type
        ? hotspot.type.replace(/_/g, " ")
        : "";

  // ZAL10-OPTION-FIELD-1: null = no "Option:" line.
  const option = normalizeHotspotOption(hotspot.option);

  // PARTS-SERVING-1: extra style keys ONLY when needed, so a card without
  // the new fields keeps today's exact markup.
  const modeStyle: React.CSSProperties = interactive
    ? {
        maxWidth: Math.max(160, Math.min(MAX_WIDTH, area.right - area.left - 8)),
        maxHeight,
        overflowY: "auto",
        overscrollBehavior: "contain",
      }
    : hasBodyExtras
      ? { maxHeight: Math.min(HOVER_EXTRAS_MAX_HEIGHT, viewH - 8), overflow: "hidden" }
      : {};

  return (
    <div
      ref={measuredRef}
      className={interactive ? CARD_CHROME : `pointer-events-none ${CARD_CHROME}`}
      {...(interactive
        ? { role: "group", "aria-label": hotspot.label, "data-hotspot-card": "pinned" }
        : {})}
      style={{
        position: "fixed",
        left,
        top,
        borderRadius: 4,
        fontSize: 12,
        padding: "8px 12px",
        maxWidth: MAX_WIDTH,
        lineHeight: 1.6,
        zIndex: 1000,
        whiteSpace: "pre-line",
        ...modeStyle,
      }}
    >
      <b style={{ fontSize: 13 }}>{hotspot.label}</b>
      {displayType && (
        <div className="mt-[2px] text-[11px] text-midas-muted-stone">
          {translate("midas.artifact.diagram.componentType")}: {displayType}
        </div>
      )}
      {hotspot.description && (
        <div className="mt-1">
          {hotspot.description}
        </div>
      )}
      {/* PARTS-SERVING-1 — parts groups, details, sheet links. */}
      {hasBodyExtras && (
        <DiagramInfoCardExtrasV2
          extras={extras}
          onOpenLink={interactive ? onOpenLink : undefined}
        />
      )}
      {/* ZAL10-OPTION-FIELD-1 — always the LAST line of the card. */}
      {option && (
        <div className="mt-1" data-hotspot-option="true">
          {translate("midas.artifact.diagram.option")}: {option}
        </div>
      )}
    </div>
  );
};

export default React.memo(DiagramInfoCardV2);
